import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { EmberField } from '@/components/shared/EmberField'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'

type ExportType = 'card' | 'itinerary' | 'phone' | 'share'

export function JourneyCardScreen() {
  const { selectedDestination, dnaKey, setScreen, resetJourney } = useAppStore()
  const [activeExport, setActiveExport] = useState<ExportType>('card')
  const [phoneMode, setPhoneMode] = useState<'sms' | 'whatsapp'>('sms')
  const [phoneSent, setPhoneSent] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')

  const profile = DNA_PROFILES[dnaKey]
  const dest = selectedDestination ?? (DESTINATIONS[dnaKey]?.[0] || null)
  if (!dest || !profile) { setScreen('journey'); return null }

  const exports = [
    { type: 'card' as ExportType,      icon: '✦', title: 'Journey Card',    desc: 'Your sacred DNA, destinations & pilgrim savings' },
    { type: 'itinerary' as ExportType, icon: '☰', title: 'Full Itinerary',   desc: 'The complete day-by-day pilgrimage' },
    { type: 'phone' as ExportType,     icon: '📱', title: 'Send to Phone',    desc: 'Receive it by SMS or WhatsApp' },
    { type: 'share' as ExportType,     icon: '🔗', title: 'Share Link',      desc: 'Share with fellow travellers' },
  ]

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <EmberField count={10} style={{ position: 'fixed' }} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Topbar center="Pilgrimage Card" backTo="gate" backLabel="← Journey" />

        <div style={{ padding: 16, flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 14, alignItems: 'start' }}>

            {/* Pilgrimage Card Visual */}
            <div style={{ border: '1px solid var(--hairline)', background: 'var(--forest2)', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
              {/* Hero */}
              <div style={{ height: 120, background: dest.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,14,8,0.15), rgba(5,14,8,0.6))' }} />
                <span style={{ position: 'relative' }}>{dest.emoji}</span>
                <div style={{ position: 'absolute', top: 9, left: 11, fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>{dest.flag}</div>
                <div style={{ position: 'absolute', bottom: 9, left: 11, fontFamily: 'var(--serif-caps)', fontSize: 20, color: '#fff', letterSpacing: '0.05em' }}>{dest.name}</div>
                <div style={{ position: 'absolute', bottom: 9, right: 11, fontFamily: 'var(--serif-body)', fontSize: 10, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>{dest.faith}</div>
              </div>

              <div style={{ padding: 14 }}>
                {/* DNA row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ color: 'var(--candle2)', fontSize: 16 }}>◈</span>
                  <div>
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 13, color: 'var(--ivory)', letterSpacing: '0.04em' }}>{profile.name}</div>
                    <div style={{ fontFamily: 'var(--sans)', fontSize: 10, color: 'var(--ivory-faint)' }}>Your sacred DNA · Sanctum by Imaginarium</div>
                  </div>
                </div>

                {/* Stops */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dest.stops.slice(0, 3).map((stop, i) => (
                    <div key={i} style={{ borderTop: '1px solid var(--hairline)', paddingTop: 8 }}>
                      <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 3 }}>{stop.time}</div>
                      <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 12, color: 'var(--ivory)', letterSpacing: '0.03em' }}>{stop.name}</div>
                      <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--candle2)', letterSpacing: '0.06em', marginTop: 4, textTransform: 'uppercase' }}>{stop.spiritualNote}</div>
                    </div>
                  ))}
                </div>

                {/* Savings */}
                <div style={{ marginTop: 12, padding: 10, background: 'rgba(201,151,58,0.08)', border: '1px solid var(--hairline2)', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--candle2)', fontStyle: 'italic' }}>
                  <span>◈</span><span>Save {dest.saving} · Travelpayouts pilgrim partner offers via Sanctum</span>
                </div>

                {/* QR */}
                <div style={{ width: 40, height: 40, background: 'var(--forest)', margin: '12px auto', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 1, padding: 3, border: '1px solid var(--hairline)' }}>
                  {[1,0,1,1,0,0,1,0,1,1,0,1,1,0,1,0,0,1,1,0,1,1,0,0,1].map((v, i) => (
                    <div key={i} style={{ background: v ? 'var(--ivory)' : 'var(--forest)' }} />
                  ))}
                </div>

                <div style={{ textAlign: 'center', fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--ivory-faint)', letterSpacing: '0.1em', textTransform: 'uppercase', borderTop: '1px solid var(--hairline)', paddingTop: 8 }}>
                  Sanctum by Imaginarium · sanctum.imaginarium.club
                </div>
              </div>
            </div>

            {/* Export panel */}
            <div>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 14, color: 'var(--ivory)', marginBottom: 12, letterSpacing: '0.06em' }}>◈ Your Pilgrimage Card</div>

              {exports.map((item) => (
                <div key={item.type}>
                  <button onClick={() => {
                    if (item.type === 'itinerary') { setScreen('itinerary'); return }
                    setActiveExport(item.type); setPhoneSent(false)
                  }}
                    style={{ width: '100%', textAlign: 'left', border: activeExport === item.type ? '2px solid var(--candle)' : '1px solid var(--hairline)', padding: 11, marginBottom: 8, background: activeExport === item.type ? 'rgba(201,151,58,0.08)' : 'var(--forest2)', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, fontFamily: 'var(--serif-caps)', fontSize: 12, color: 'var(--ivory)', letterSpacing: '0.04em' }}>
                      <span style={{ color: 'var(--candle2)' }}>{item.icon}</span> {item.title}
                    </div>
                    <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory-faint)', lineHeight: 1.4, fontStyle: 'italic' }}>{item.desc}</div>
                  </button>

                  {item.type === 'card' && activeExport === 'card' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ marginBottom: 8, padding: '8px 10px', border: '1px solid var(--hairline)', background: 'rgba(201,151,58,0.05)' }}>
                      <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory-dim)', lineHeight: 1.6, fontStyle: 'italic' }}>
                        ◈ The pilgrimage card at left is your shareable summary — {profile.name} · {dest.name} · save {dest.saving}. Print it, or explore the full day-by-day pilgrimage.
                      </div>
                    </motion.div>
                  )}

                  {item.type === 'share' && activeExport === 'share' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ marginBottom: 8, padding: '8px 10px', border: '1px solid var(--hairline)', background: 'rgba(201,151,58,0.05)' }}>
                      <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory-dim)', lineHeight: 1.5, fontStyle: 'italic', marginBottom: 6 }}>
                        Share this sacred journey with fellow travellers:
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input readOnly value={`sanctum.imaginarium.club/j/${dest.id}`} style={{ flex: 1, background: 'var(--forest2)', border: '1px solid var(--hairline)', padding: '7px 9px', fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory)' }} />
                        <button onClick={() => { try { navigator.clipboard.writeText(`https://sanctum.imaginarium.club/j/${dest.id}`) } catch { /* ignore */ } }} style={{ padding: '7px 12px', background: 'var(--candle)', color: 'var(--forest)', border: 'none', fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' }}>Copy</button>
                      </div>
                    </motion.div>
                  )}

                  {item.type === 'phone' && activeExport === 'phone' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ marginBottom: 8, padding: '0 2px' }}>
                      {!phoneSent ? (
                        <>
                          <div style={{ display: 'flex', gap: 5, marginBottom: 6 }}>
                            {(['sms', 'whatsapp'] as const).map((m) => (
                              <button key={m} onClick={() => setPhoneMode(m)} style={{ flex: 1, padding: 6, fontSize: 11, fontFamily: 'var(--serif-caps)', letterSpacing: '0.06em', border: phoneMode === m ? '1px solid var(--candle)' : '1px solid var(--hairline)', background: phoneMode === m ? 'rgba(201,151,58,0.08)' : 'var(--forest2)', color: phoneMode === m ? 'var(--candle2)' : 'var(--ivory-faint)', cursor: 'pointer' }}>
                                {m === 'sms' ? '📱 SMS' : '💬 WhatsApp'}
                              </button>
                            ))}
                          </div>
                          <select style={{ width: '100%', background: 'var(--forest2)', border: '1px solid var(--hairline)', padding: '7px 9px', fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory)', marginBottom: 5 }}>
                            <option>GB +44 United Kingdom</option>
                            <option>US +1 United States</option>
                            <option>AU +61 Australia</option>
                            <option>IE +353 Ireland</option>
                            <option>IT +39 Italy</option>
                            <option>ES +34 Spain</option>
                          </select>
                          <input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} inputMode="numeric" placeholder="Your number…" style={{ width: '100%', background: 'var(--forest2)', border: '1px solid var(--hairline)', padding: '8px 10px', fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--ivory)', marginBottom: 6 }} />

                          {/* Numeric keypad — same layout/logic as the other apps, themed in Sanctum's palette */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 6 }}>
                            {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((k, ki) => (
                              <button key={ki} type="button" disabled={!k}
                                onClick={() => {
                                  if (k === '⌫') setPhoneNumber((p) => p.slice(0, -1))
                                  else if (k) setPhoneNumber((p) => p + k)
                                }}
                                style={{
                                  padding: '7px 0', fontFamily: 'var(--serif-caps)', fontSize: 12, letterSpacing: '0.04em',
                                  border: '1px solid var(--hairline)', background: k ? 'var(--forest2)' : 'transparent',
                                  color: 'var(--ivory)', cursor: k ? 'pointer' : 'default',
                                  opacity: k ? 1 : 0, pointerEvents: k ? 'auto' : 'none',
                                }}>
                                {k}
                              </button>
                            ))}
                          </div>

                          <button onClick={() => setPhoneSent(true)} style={{ width: '100%', padding: 9, background: 'var(--candle)', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.12em', border: 'none', cursor: 'pointer', textTransform: 'uppercase', boxShadow: '0 0 14px rgba(201,151,58,0.35)' }}>
                            Send now
                          </button>
                        </>
                      ) : (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: 'center', padding: 12 }}>
                          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 16, color: 'var(--candle2)', marginBottom: 5, fontStyle: 'italic' }}>Sent ✓</div>
                          <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--ivory-dim)', fontStyle: 'italic' }}>
                            On its way — your pilgrimage and all sacred partner offers await.
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </div>
              ))}

              <button onClick={() => window.print()} style={{ width: '100%', padding: 12, background: 'var(--candle)', border: 'none', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.12em', cursor: 'pointer', textTransform: 'uppercase', marginBottom: 8, boxShadow: '0 0 18px rgba(201,151,58,0.35)' }}>◈ Print Pilgrimage Card</button>

              <button onClick={resetJourney} style={{ width: '100%', padding: 10, background: 'var(--forest2)', border: '1px solid var(--hairline)', color: 'var(--ivory-dim)', fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.1em', cursor: 'pointer', textTransform: 'uppercase' }}>+ Begin a new journey</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
